import React, { useState, useEffect } from 'react';
import { Tab, Button, Icon } from 'semantic-ui-react';
import { ChromePicker } from 'react-color';
import { useBackgrounds } from '../../hooks/useBackgrounds';
import './BackgroundCustomizer.css';

const BACKGROUND_COLOR_PRESETS = [
  '#FFFFFF', // Default beige
  '#FFFFFF', // White
  '#F8F9FA', // Light gray
  '#E9ECEF', // Gray
  '#DEE2E6', // Medium gray
  '#ADB5BD', // Dark gray
  '#6C757D', // Darker gray
  '#343A40', // Very dark gray
  '#000000', // Black
  '#FFF3E0', // Light orange
  '#FFE0B2', // Peach
  '#FFCCBC', // Light coral
  '#F8BBD9', // Light pink
  '#E1BEE7', // Light purple
  '#C5CAE9', // Light blue
  '#BBDEFB', // Sky blue
  '#B2DFDB', // Light teal
  '#C8E6C9', // Light green
  '#DCEDC8', // Light lime
];

const GRADIENT_PRESETS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
];

const BACKGROUND_PRESETS = {
  Studio: [
    {
      id: 'studio-white',
      name: 'Studio White',
      style: { backgroundColor: '#FFFFFF' },
      thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRkZGRkZGIiBzdHJva2U9IiNFMEUwRTAiLz4KPHN2Zz4K'
    },
    {
      id: 'studio-gray',
      name: 'Studio Gray',
      style: { backgroundColor: '#F8F9FA' },
      thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRjhGOUZBIiBzdHJva2U9IiNFMEUwRTAiLz4KPHN2Zz4K'
    },
    {
      id: 'studio-black',
      name: 'Studio Black',
      style: { backgroundColor: '#000000' },
      thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjMDAwMDAwIiBzdHJva2U9IiNFMEUwRTAiLz4KPHN2Zz4K'
    }
  ],
  Room: [
    {
      id: 'room-bedroom',
      name: 'Bedroom',
      style: { backgroundColor: '#FFF8E7' },
      thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRkZGOEU3IiBzdHJva2U9IiNFMEUwRTAiLz4KPHN2Zz4K'
    },
    {
      id: 'room-closet',
      name: 'Closet',
      style: { backgroundColor: '#F0F4F8' },
      thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRjBGNEY4IiBzdHJva2U9IiNFMEUwRTAiLz4KPHN2Zz4K'
    },
    {
      id: 'room-lounge',
      name: 'Lounge',
      style: { backgroundColor: '#FDF2F8' },
      thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRkRGMkY4IiBzdHJva2U9IiNFMEUwRTAiLz4KPHN2Zz4K'
    }
  ],
  Outdoor: [
    {
      id: 'outdoor-sky',
      name: 'Sky Blue',
      style: { background: 'linear-gradient(180deg, #87CEEB 0%, #E0F6FF 100%)' },
      thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0ic2t5IiB4MT0iMCIgeTE9IjAiIHgyPSIwIiB5Mj0iMSI+CjxzdG9wIG9mZnNldD0iMCUiIHN0b3AtY29sb3I9IiM4N0NFRUIiLz4KPHN0b3Agb2Zmc2V0PSIxMDAlIiBzdG9wLWNvbG9yPSIjRTBGNkZGIi8+CjwvbGluZWFyR3JhZGllbnQ+CjwvZGVmcz4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSJ1cmwoI3NreSkiIHN0cm9rZT0iI0UwRTBFMCIvPgo8L3N2Zz4K'
    },
    {
      id: 'outdoor-nature',
      name: 'Nature Green',
      style: { background: 'linear-gradient(180deg, #90EE90 0%, #F0FFF0 100%)' },
      thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0ibmF0dXJlIiB4MT0iMCIgeTE9IjAiIHgyPSIwIiB5Mj0iMSI+CjxzdG9wIG9mZnNldD0iMCUiIHN0b3AtY29sb3I9IiM5MEVFOTAiLz4KPHN0b3Agb2Zmc2V0PSIxMDAlIiBzdG9wLWNvbG9yPSIjRjBGRkYwIi8+CjwvbGluZWFyR3JhZGllbnQ+CjwvZGVmcz4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSJ1cmwoI25hdHVyZSkiIHN0cm9rZT0iI0UwRTBFMCIvPgo8L3N2Zz4K'
    }
  ],
  Abstract: [
    {
      id: 'abstract-sunset',
      name: 'Sunset',
      style: { background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)' },
      thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0ic3Vuc2V0IiB4MT0iMCIgeTE9IjAiIHgyPSIxIiB5Mj0iMSI+CjxzdG9wIG9mZnNldD0iMCUiIHN0b3AtY29sb3I9IiNGRjZCNkIiLz4KPHN0b3Agb2Zmc2V0PSIxMDAlIiBzdG9wLWNvbG9yPSIjRkZFNjZEIi8+CjwvbGluZWFyR3JhZGllbnQ+CjwvZGVmcz4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSJ1cmwoI3N1bnNldCkiIHN0cm9rZT0iI0UwRTBFMCIvPgo8L3N2Zz4K'
    },
    {
      id: 'abstract-ocean',
      name: 'Ocean',
      style: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
      thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0ib2NlYW4iIHgxPSIwIiB5MT0iMCIgeDI9IjEiIHkyPSIxIj4KPHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iIzY2N2VlYSIvPgo8c3RvcCBvZmZzZXQ9IjEwMCUiIHN0b3AtY29sb3I9IiM3NjRiYTIiLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgo8cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIGZpbGw9InVybCgjb2NlYW4pIiBzdHJva2U9IiNFMEUwRTAiLz4KPHN2Zz4K'
    }
  ]
};

const BackgroundCustomizer = ({
  className = ''
}) => {
  // Use the new useBackgrounds hook
  const {
    background: currentBackground,
    userBackgrounds,
    publicBackgrounds,
    isLoading,
    isUploading,
    isDeleting,
    error,
    setBackgroundColor,
    setBackgroundGradient,
    setBackgroundImage,
    setBackgroundPreset,
    uploadBackground,
    deleteBackground,
    clearError
  } = useBackgrounds();
  
  const [activeTab, setActiveTab] = useState(0);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customColor, setCustomColor] = useState(currentBackground.color || '#FFFFFF');
  
  // Update custom color when background changes
  useEffect(() => {
    if (currentBackground.type === 'color' && currentBackground.color) {
      setCustomColor(currentBackground.color);
    }
  }, [currentBackground]);

  const handleColorChange = (color) => {
    setCustomColor(color.hex);
    setBackgroundColor(color.hex);
  };

  const handlePresetColorSelect = (color) => {
    setCustomColor(color);
    setBackgroundColor(color);
  };

  const handleGradientSelect = (gradient) => {
    setBackgroundGradient(gradient);
  };

  const handlePresetSelect = (preset) => {
    setBackgroundPreset(preset);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      await handleImageUploadToBackend(file);
    }
  };

  const handleImageDrop = async (file) => {
    if (file) {
      await handleImageUploadToBackend(file);
    }
  };
  
  const handleImageUploadToBackend = async (file) => {
    // Validate file
    if (!file.type.startsWith('image/')) {
      // Error handling is done in the hook
      return;
    }
    
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      // Error handling is done in the hook
      return;
    }
    
    try {
      clearError();
      // Upload to backend using the hook (automatically sets as active background)
      await uploadBackground({
        file,
        name: file.name,
        description: 'Custom background',
        category: 'custom',
        isPublic: false
      });
    } catch (err) {
      console.error('Background upload failed:', err);
    }
  };

  const panes = [
    {
      menuItem: { key: 'colors', icon: 'paint brush', content: 'Colors' },
      render: () => (
        <Tab.Pane className="background-tab-pane colors-tab">
          <div className="color-section">
            <h4>Custom Color</h4>
            <div className="custom-color-controls">
              <Button
                className="color-picker-trigger"
                style={{ backgroundColor: customColor }}
                onClick={() => setShowColorPicker(!showColorPicker)}
              >
                <Icon name="tint" />
              </Button>
              {showColorPicker && (
                <div className="color-picker-popover">
                  <div 
                    className="color-picker-cover" 
                    onClick={() => setShowColorPicker(false)}
                  />
                  <ChromePicker 
                    color={customColor}
                    onChange={handleColorChange}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="color-section">
            <h4>Preset Colors</h4>
            <div className="color-grid">
              {BACKGROUND_COLOR_PRESETS.map((color, index) => (
                <div
                  key={index}
                  className={`color-option ${currentBackground.color === color ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => handlePresetColorSelect(color)}
                  title={color}
                />
              ))}
            </div>
          </div>

          <div className="color-section">
            <h4>Gradients</h4>
            <div className="gradient-grid">
              {GRADIENT_PRESETS.map((gradient, index) => (
                <div
                  key={index}
                  className={`gradient-option ${currentBackground.gradient === gradient ? 'selected' : ''}`}
                  style={{ background: gradient }}
                  onClick={() => handleGradientSelect(gradient)}
                />
              ))}
            </div>
          </div>
        </Tab.Pane>
      )
    },
    {
      menuItem: { key: 'images', icon: 'image', content: 'Images' },
      render: () => (
        <Tab.Pane className="background-tab-pane images-tab">
          <div 
            className={`upload-area ${isLoading ? 'loading' : ''}`}
            onClick={() => !isLoading && document.getElementById('background-image-upload').click()}
            onDragOver={(e) => {
              e.preventDefault();
              if (!isLoading) {
                e.currentTarget.classList.add('drag-over');
              }
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('drag-over');
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('drag-over');
              if (!isLoading) {
                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  handleImageDrop(files[0]);
                }
              }
            }}
          >
            <input
              type="file"
              id="background-image-upload"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              disabled={isLoading}
            />
            {isLoading ? (
              <>
                <Icon name="spinner" loading size="large" />
                <h4>Uploading Background Image...</h4>
                <p>Please wait while we process your image</p>
              </>
            ) : (
              <>
                <Icon name="upload" size="large" />
                <h4>Upload Background Image</h4>
                <p>Click to select or drag and drop</p>
                <p className="upload-hint">
                  Supported formats: JPG, PNG, GIF (max 5MB)
                </p>
              </>
            )}
            {error && (
              <div className="error-message">
                <Icon name="exclamation triangle" color="red" />
                <span>{error}</span>
                <Button 
                  size="mini" 
                  basic 
                  onClick={() => clearError()}
                  style={{ marginLeft: '8px' }}
                >
                  Dismiss
                </Button>
              </div>
            )}
          </div>

          {/* User's Uploaded Backgrounds */}
          {userBackgrounds && userBackgrounds.length > 0 && (
            <div className="user-backgrounds-section">
              <h4>Your Backgrounds ({userBackgrounds.length})</h4>
              <div className="backgrounds-grid">
                {userBackgrounds.map((bg) => (
                  <div
                    key={bg.id}
                    className={`background-card ${currentBackground.imageUrl === bg.image?.url ? 'selected' : ''}`}
                    onClick={() => setBackgroundImage(bg.image?.url, null, bg.id)}
                  >
                    <div className="background-thumbnail">
                      <img 
                        src={bg.thumbnail?.url || bg.image?.url} 
                        alt={bg.name}
                      />
                    </div>
                    <div className="background-info">
                      <p className="background-name">{bg.name}</p>
                      {bg.lastUsed && (
                        <p className="background-meta">
                          Last used: {new Date(bg.lastUsed).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Button
                      icon="trash"
                      size="mini"
                      basic
                      color="red"
                      className="delete-background-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Delete this background?')) {
                          deleteBackground(bg.id);
                        }
                      }}
                      loading={isDeleting}
                      title="Delete background"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Public Backgrounds */}
          {publicBackgrounds && publicBackgrounds.length > 0 && (
            <div className="public-backgrounds-section">
              <h4>Public Backgrounds</h4>
              <div className="backgrounds-grid">
                {publicBackgrounds.slice(0, 6).map((bg) => (
                  <div
                    key={bg.id}
                    className={`background-card ${currentBackground.imageUrl === bg.image?.url ? 'selected' : ''}`}
                    onClick={() => setBackgroundImage(bg.image?.url, null, bg.id)}
                  >
                    <div className="background-thumbnail">
                      <img 
                        src={bg.thumbnail?.url || bg.image?.url} 
                        alt={bg.name}
                      />
                    </div>
                    <div className="background-info">
                      <p className="background-name">{bg.name}</p>
                      <p className="background-meta">
                        <Icon name="heart" /> {bg.usageCount || 0} uses
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Tab.Pane>
      )
    },
    {
      menuItem: { key: 'presets', icon: 'grid layout', content: 'Presets' },
      render: () => (
        <Tab.Pane className="background-tab-pane presets-tab">
          {Object.entries(BACKGROUND_PRESETS).map(([category, presets]) => (
            <div key={category} className="preset-category">
              <h4>{category} Backgrounds</h4>
              <div className="preset-grid">
                {presets.map((preset) => (
                  <div
                    key={preset.id}
                    className={`preset-card ${currentBackground.preset?.id === preset.id ? 'selected' : ''}`}
                    onClick={() => handlePresetSelect(preset)}
                    title={preset.name}
                  >
                    <div 
                      className="preset-preview"
                      style={preset.style}
                    >
                      {preset.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Tab.Pane>
      )
    }
  ];

  return (
    <div className={`background-customizer ${className}`}>
      <div className="background-customizer-header">
        <Icon name="paint brush" />
        <h3>Background</h3>
      </div>
      <Tab 
        panes={panes} 
        activeIndex={activeTab}
        onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
        className="background-tabs"
      />
    </div>
  );
};

export default BackgroundCustomizer;